import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Instagram, ArrowRight } from "lucide-react";

/**
 * Reusable Instagram feed section.
 *   - placement: "landing" | "social" | "footer"
 *   - variant: "grid" (default for landing/social) | "strip" (compact horizontal for footer)
 */
export default function InstagramFeed({ placement = "landing", limit, variant = "grid", title = "Latest from Instagram", showHeader = true }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/instagram/${placement}${limit ? `?limit=${limit}` : ""}`);
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [placement, limit]);

  if (items === null) {
    return variant === "strip" ? null : (
      <div className={`grid ${variant === "grid" ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-6" : ""} gap-2`} data-testid={`ig-feed-${placement}-loading`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="aspect-square bg-[#1A1A1A] animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return null; // Gracefully hide if nothing curated yet
  }

  if (variant === "strip") {
    return (
      <div className="flex items-center gap-3 overflow-x-auto whitespace-nowrap py-1" data-testid={`ig-feed-${placement}`}>
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold shrink-0">
          <Instagram className="h-3 w-3 inline mr-1.5 text-[#FF331F]" />@flow_state_method
        </span>
        {items.map((it) => (
          <a key={it.ig_id} href={it.permalink} target="_blank" rel="noreferrer" className="shrink-0 block w-12 h-12 overflow-hidden border border-[#27272A] hover:border-[#FF331F]/60 transition-colors" data-testid={`ig-strip-${it.ig_id}`}>
            <img src={it.thumbnail_url || it.media_url} alt={it.caption?.slice(0, 60) || "Instagram post"} className="w-full h-full object-cover" loading="lazy" />
          </a>
        ))}
      </div>
    );
  }

  return (
    <section className="my-16 md:my-24" data-testid={`ig-feed-${placement}`}>
      {showHeader && (
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-[#FF331F] text-[10px] uppercase tracking-[0.3em] font-bold mb-2 flex items-center gap-2">
              <Instagram className="h-3 w-3" /> @flow_state_method
            </div>
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight">{title}</h2>
          </div>
          <a href="https://www.instagram.com/flow_state_method/" target="_blank" rel="noreferrer" className="text-[#FF331F] text-xs uppercase tracking-widest font-bold hover:underline flex items-center">
            Follow on Instagram <ArrowRight className="h-3 w-3 ml-1" />
          </a>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
        {items.map((it) => (
          <a key={it.ig_id} href={it.permalink} target="_blank" rel="noreferrer" className="group relative block aspect-square overflow-hidden bg-[#1A1A1A]" data-testid={`ig-card-${it.ig_id}`}>
            <img src={it.thumbnail_url || it.media_url} alt={it.caption?.slice(0, 80) || "Instagram post"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            {it.media_type === "VIDEO" && (
              <span className="absolute top-2 right-2 bg-black/70 text-white text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5">Video</span>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
              <span className="text-white text-xs line-clamp-3">{it.caption?.slice(0, 120) || ""}</span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
